import { computed } from 'vue';

import type { ConditionalRequestDefinition } from '@/utils/dehydrateRequestData/types';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';
import { usePkwInputStore } from '@/features/Sparten/pkw/useInputStore';
import { FAHRERKREIS_TYP } from '@/features/Sparten/shared/types';
import { KfzFahrerResource } from '@/features/Sparten/shared/schema/kfz/clientSchema/clientSchema';
import { useInputStore } from '@/features/Sparten';

export const isCompanyFahrerkreisWithoutWohneigentum: ConditionalRequestDefinition<KfzFahrerResource> =
  {
    if: (entity) => computed(() => !entity.meta.hasWohneigentum),
    drop: ['meta.wohneigentum'],
  };

export const isCompanyFahrerkreisFix: ConditionalRequestDefinition = {
  if: computed(
    () =>
      useInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.FESTGELEGT &&
      useInputStore().versicherungsnehmer.isFirma,
  ),
  drop: ['ort', 'meta.inHaeuslicherGemeinschaftMitVN'],
};

export const isCompanyFahrerkreisWithGermanDriver: ConditionalRequestDefinition<KfzFahrerResource> =
  {
    if: (entity) =>
      computed(() => {
        return getSimpleChoiceMeta(
          SIMPLE_CHOICE.NATIONALITY,
          SIMPLE_CHOICE_META.NATIONALITY_GERMAN,
        ).includes(entity.nationalitaet);
      }),
    drop: ['meta.hasWohnsitzMin5JahreInBrd'],
  };

export const isVnHauptnutzer: ConditionalRequestDefinition = {
  if: computed(() => useInputStore().fahrerkreis.isVnHauptnutzer),
  drop: ['meta.isHauptnutzer'],
};

export const isCompanyFahrerkreisWithoutMinors: ConditionalRequestDefinition<KfzFahrerResource> =
  {
    if: (entity) =>
      computed(() => {
        return getSimpleChoiceMeta(
          SIMPLE_CHOICE.MINOR_CHILDREN,
          SIMPLE_CHOICE_META.MINOR_CHILDREN_KEIN_KIND,
        ).includes(entity.meta.hasMinderjaehrigeKinder);
      }),
    drop: ['meta.minderjaehrigeKinder'],
  };

export const isNutzerWithAMinor: ConditionalRequestDefinition<KfzFahrerResource> =
  {
    if: (entity) =>
      computed(() => {
        return getSimpleChoiceMeta(
          SIMPLE_CHOICE.MINOR_CHILDREN,
          SIMPLE_CHOICE_META.MINOR_CHILDREN_EIN_KIND,
        ).includes(entity.meta.hasMinderjaehrigeKinder);
      }),
    drop: [
      'meta.minderjaehrigeKinder.alterAeltestesKind',
      'meta.minderjaehrigeKinder.alterJuengstesKind',
    ],
  };

export const isNutzerWithSeveralMinors: ConditionalRequestDefinition<KfzFahrerResource> =
  {
    if: (entity) =>
      computed(() => {
        return getSimpleChoiceMeta(
          SIMPLE_CHOICE.MINOR_CHILDREN,
          SIMPLE_CHOICE_META.MINOR_CHILDREN_HAS_MEHRERE_KINDER,
        ).includes(entity.meta.hasMinderjaehrigeKinder);
      }),
    drop: ['meta.minderjaehrigeKinder.alter'],
  };

export const isNutzerVorsteuerabzugsberechtigt: ConditionalRequestDefinition<KfzFahrerResource> =
  {
    if: (entity) =>
      computed(() => {
        return !getSimpleChoiceMeta(
          SIMPLE_CHOICE.KFZ_BERUFSSTATUS,
          SIMPLE_CHOICE_META.KFZ_BERUFSSTATUS_IS_VORSTEUER_ABZUGS_BERECHTIGT,
        ).includes(entity.meta.berufsstatus);
      }),
    drop: ['meta.isVorsteuerAbzugsberechtigt'],
  };

export const isCompanyFahrerkreisFixNoSingleUser: ConditionalRequestDefinition<KfzFahrerResource> =
  {
    if: (entity) =>
      computed(() => {
        return !getSimpleChoiceMeta(
          SIMPLE_CHOICE.BEZUG_ZUM_VN_JURISTISCHE_PERSON,
          SIMPLE_CHOICE_META.BEZUG_ZUM_VN_IS_EINZELNUTZER,
        ).includes(entity.meta.bezugZumVn);
      }),
    drop: [
      'ort',
      'beruf',
      'branche',
      'familienstand',
      'nationalitaet',
      'meta.hasFuehrerscheinInEUErworben',
      'meta.hasAnSicherheitstrainingTeilgenommen',
      'meta.hasJahreskarteOeffentlicherPersonenverkehr',
      'meta.jahreskarteOeffentlicherPersonenverkehr',
      'meta.hasMinderjaehrigeKinder',
      'meta.minderjaehrigeKinder',
      'meta.hasWohneigentum',
      'meta.hasWohnsitzMin5JahreInBrd',
      'meta.isMitarbeiterAutohersteller',
      'meta.isVorsteuerAbzugsberechtigt',
      'meta.hasAnSicherheitstrainingTeilgenommen',
      'meta.mitgliedschaften',
      'meta.wohneigentum',
      'meta.berufsstatus',
      'meta.speziellerArbeitgeber',
      'meta.tarifgruppe',
    ],
  };

export const isFahrerkreisFixAndVnIsNotACompany: ConditionalRequestDefinition =
  {
    if: computed(
      () =>
        usePkwInputStore().fahrerkreis.typ === FAHRERKREIS_TYP.FESTGELEGT &&
        usePkwInputStore().versicherungsnehmer.isFirma === false,
    ),
    drop: [
      'ort',
      'berufsstatus',
      'beruf',
      'branche',
      'speziellerArbeitgeber',
      'tarifgruppe',
      'familienstand',
      'nationalitaet',
      'meta.hasFuehrerscheinInEUErworben',
      'meta.hasAnSicherheitstrainingTeilgenommen',
      'meta.hasJahreskarteOeffentlicherPersonenverkehr',
      'meta.jahreskarteOeffentlicherPersonenverkehr',
      'meta.hasMinderjaehrigeKinder',
      'meta.minderjaehrigeKinder',
      'meta.hasWohneigentum',
      'meta.hasWohnsitzMin5JahreInBrd',
      'meta.isMitarbeiterAutohersteller',
      'meta.isVorsteuerAbzugsberechtigt',
      'meta.mitgliedschaften',
      'meta.wohneigentum',
    ],
  };

export const temporaryFieldDriverMeta: ConditionalRequestDefinition = {
  if: computed(() => true),
  drop: ['meta.isFullAge'],
};

export const kfzInputClientEntityDehydrationDefinitions: ConditionalRequestDefinition<KfzFahrerResource>[] =
  [
    isCompanyFahrerkreisWithoutWohneigentum,
    isCompanyFahrerkreisFix,
    isCompanyFahrerkreisWithGermanDriver,
    isVnHauptnutzer,
    isCompanyFahrerkreisWithoutMinors,
    isNutzerWithAMinor,
    isNutzerWithSeveralMinors,
    isNutzerVorsteuerabzugsberechtigt,
    isCompanyFahrerkreisFixNoSingleUser,
    isFahrerkreisFixAndVnIsNotACompany,
    temporaryFieldDriverMeta,
  ];
